import { vibeAgentLogger as logger } from 'v2/utils/logger'
import type { GenericEdge, GenericNode } from '../../types'

/**
 * Delete-safe canvas reconcile for background-run reattach.
 *
 * The live-tail workflow updater only adds/updates (deletions are discrete
 * events), so on mount/reattach this detects only what must be REMOVED because
 * it is absent from the authoritative snapshot. Adds/updates are NOT diffed
 * here — the caller hands the snapshot to applyWorkflowUpdate, which already
 * re-diffs by id+data and no-ops when nothing changed. Pure; unit tested.
 */

export interface WorkflowSnapshot {
  nodes: GenericNode[]
  edges: GenericEdge[]
}

export interface SnapshotDiff {
  nodeIdsToRemove: string[]
  edgeIdsToRemove: string[]
}

export const parseWorkflowSnapshot = (json?: string | null): WorkflowSnapshot | null => {
  if (!json) return null
  try {
    const parsed = JSON.parse(json)
    if (!Array.isArray(parsed?.nodes) || !Array.isArray(parsed?.edges)) return null
    return { nodes: parsed.nodes as GenericNode[], edges: parsed.edges as GenericEdge[] }
  } catch (e) {
    logger.warn('Failed to parse workflow snapshot', e)
    return null
  }
}

export const diffWorkflowSnapshot = (
  current: WorkflowSnapshot,
  snapshot: WorkflowSnapshot,
): SnapshotDiff => {
  const snapshotNodeIds = new Set(snapshot.nodes.map((n) => n.id))
  const snapshotEdgeIds = new Set(snapshot.edges.map((e) => e.id))
  return {
    nodeIdsToRemove: current.nodes.filter((n) => !snapshotNodeIds.has(n.id)).map((n) => n.id),
    edgeIdsToRemove: current.edges.filter((e) => !snapshotEdgeIds.has(e.id)).map((e) => e.id),
  }
}
